import * as React from "react";
import { motion } from "framer-motion";
import { useMemo } from "react";
import { ResearchChatMessage } from "../../types/research.types";
import MessageLoading from "./MessageLoading";
import Message from "./Message";
import { Loader } from "lucide-react";
import { useSendResearchMessage } from "@/hooks/useSendResearchMessage";
import { useDocument } from "@/hooks/useDocument";
import ChatInputWithGallery from "./ChatInputWithGallery"; // Import the new component
import { useUser } from "@/hooks/useUser";
import ReactMarkdown from "react-markdown";
import ScrollToBottomButton from "./ScrollToBottomButton";

interface ChatDetailProps {
  messages: ResearchChatMessage[];
  isLoading?: boolean;
  chatId: string;
  onNewMessage?: () => void;
}

const ChatDetail: React.FC<ChatDetailProps> = ({ messages, isLoading, chatId, onNewMessage }) => {
  const { data: doc } = useDocument();
  const lastMessageRef = React.useRef<HTMLDivElement>(null);
  const sendMessageMutation = useSendResearchMessage();
  const { user } = useUser();

  const welcomeMessage = useMemo(() => {
    return `**Hello ${user?.first_name || "Myra"}! I'm Amanda,**

I am your legal research assistant.

I can look through all local case law and legislation to help you find information quickly.

**Please provide me with clear and explicit search requests.**

For best results, please don't ask me to do 10 different things at once. Break your instructions into multiple steps.`;
  }, [user?.first_name]);

  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    lastMessageRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  React.useEffect(() => {
    scrollToBottom();
  }, [messages, sendMessageMutation.isPending]);

  const handleSendMessage = async (message: string) => {
    if (message.trim()) {
      return new Promise<void>((resolve, reject) => {
        sendMessageMutation.mutate({
          chat_id: chatId,
          data: message.trim(),
          document_id: doc?._id ? [doc?._id] : undefined,
          audio: false,
        }, {
          onSuccess: () => {
            if (onNewMessage) {
              onNewMessage();
            }
            resolve();
          },
          onError: (err) => {
            reject(err);
          }
        });
      });
    }
  };

  // handleRequestSelect is no longer needed here as ChatInputWithGallery directly calls onSendMessage

  if (isLoading) {
    return (
      <div className="flex flex-col h-full items-center justify-center p-8">
        <Loader className="animate-spin" />
        <p>Loading messages...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0 relative flex flex-col">
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-auto pb-0 flex flex-col gap-3"
        >
          {messages.length === 0 ? (
            <div className="px-4 py-3 rounded-lg text-sm bg-neutral-700 text-neutral-200">
              <div className="flex-1 prose prose-sm prose-li:marker:text-neutral-200 max-w-none">
                <ReactMarkdown
                  components={{
                    a: ({ node, ...props }) => (
                      <a {...props} target="_blank" rel="noopener noreferrer" />
                    ),
                  }}
                >
                  {welcomeMessage}
                </ReactMarkdown>
              </div>
            </div>
          ) : (
            messages.map((message, index) => {
              const isLastMessage = index === messages.length - 1;
              return (
                <React.Fragment key={index}>
                  {message.question && (
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      className="w-full"
                      ref={isLastMessage && !message.response ? lastMessageRef : null}
                    >
                      <Message
                        type="user"
                        message={message.question}
                        creation_date={message.creation_date}
                      />
                    </motion.div>
                  )}

                  {message.response && (
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      className="w-full"
                      ref={isLastMessage ? lastMessageRef : null}
                    >
                      <Message
                        type="assistant"
                        message={message.response}
                        creation_date={message.creation_date}
                        messageObject={message}
                        relatedQuestions={message.related_questions}
                        onRelatedQuestionClick={(question) => handleSendMessage(question)}
                        requestType="research"
                        documentId={doc?._id}
                        documentTitle={doc?.title}
                      />
                    </motion.div>
                  )}
                </React.Fragment>
              );
            })
          )}
          {sendMessageMutation.isPending && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="w-full"
            >
              <MessageLoading text="AI is researching" />
            </motion.div>
          )}
        </div>
        <ScrollToBottomButton targetRef={lastMessageRef} containerRef={scrollContainerRef} dependency={messages} />
      </div>
      <ChatInputWithGallery
        onSendMessage={handleSendMessage}
        isSendingMessage={sendMessageMutation.isPending}
        showCompassButton={false} // Compass button not shown in ChatDetail
        requestType="research"
      />
    </div>
  );
};

export default ChatDetail;
